import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from 'smart-react';
import { RefreshCw, Webhook } from 'lucide-react';

import { Button } from '@/Modules/Core/Components/shadcn/button';
import { AppPageSection } from '@/Modules/Core/Components/Layout/AppPage';
import { EmptyState } from '@/Modules/Core/Components/Layout/EmptyState';
import { PaginationControls } from '@/Modules/Core/Components/Layout/PaginationControls';
import { cn } from '@/lib/utils';
import WebHookLogDetailView from '../Components/WebHookTimeline/WebHookLogDetailView';
import WebHookLogFeedFilters from '../Components/WebHookTimeline/WebHookLogFeedFilters';
import WebHookLogFeedSkeleton from '../Components/WebHookTimeline/WebHookLogFeedSkeleton';
import WebHookTimelineFeed from '../Components/WebHookTimeline/WebHookTimelineFeed';
import { WEBHOOK_PAGE_SIZE } from '../Components/WebHookTimeline/webHookTimelineUtils';
import useWebHookTimeline, {
  DEFAULT_WEBHOOK_LOG_FILTERS,
} from '../hooks/useWebHookTimeline';

const getAlertClassName = (type) => {
  if (type === 'success') {
    return 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200';
  }

  return 'border-destructive/20 bg-destructive/10 text-destructive';
};

const formatSuccessRate = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '0%';
  }

  return `${Number.isInteger(numericValue) ? numericValue : numericValue.toFixed(2)}%`;
};

const formatStatusOptionLabel = (statusKey) => {
  if (statusKey === 'success') {
    return 'Success';
  }

  if (statusKey === 'failed') {
    return 'Fail';
  }

  if (statusKey === 'unknown') {
    return 'Other';
  }

  if (!statusKey) {
    return 'Unknown';
  }

  return statusKey.charAt(0).toUpperCase() + statusKey.slice(1);
};

const areFiltersEqual = (leftFilters, rightFilters) =>
  Object.keys(DEFAULT_WEBHOOK_LOG_FILTERS).every(
    (key) => leftFilters?.[key] === rightFilters?.[key]
  );

const isDateWithinRange = (value, fromDate, toDate) => {
  if (!value) {
    return false;
  }

  const targetDate = new Date(value);

  if (Number.isNaN(targetDate.getTime())) {
    return false;
  }

  if (fromDate) {
    const fromBoundary = new Date(fromDate);
    fromBoundary.setHours(0, 0, 0, 0);

    if (targetDate < fromBoundary) {
      return false;
    }
  }

  if (toDate) {
    const toBoundary = new Date(toDate);
    toBoundary.setHours(23, 59, 59, 999);

    if (targetDate > toBoundary) {
      return false;
    }
  }

  return true;
};

const WebHookLogs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = React.useState({
    ...DEFAULT_WEBHOOK_LOG_FILTERS,
  });
  const [debouncedFilters, setDebouncedFilters] = React.useState({
    ...DEFAULT_WEBHOOK_LOG_FILTERS,
  });
  const [pageNumber, setPageNumber] = React.useState(1);
  const [alertMessage, setAlertMessage] = React.useState({
    type: 'error',
    message: '',
  });

  const {
    logs,
    loading,
    refreshing,
    errorMessage,
    totalRecordCount,
    stats,
    pagination,
    refreshLogs,
  } = useWebHookTimeline({
    filters: debouncedFilters,
    pageNumber,
    pageSize: WEBHOOK_PAGE_SIZE,
  });

  React.useEffect(() => {
    if (areFiltersEqual(filters, debouncedFilters)) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      const nextFilters = {
        ...filters,
      };

      setDebouncedFilters(nextFilters);
      setPageNumber(1);
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [debouncedFilters, filters]);

  const selectedDeliveryId = String(searchParams.get('delivery') ?? '').trim();

  const clearAlert = React.useCallback(() => {
    setAlertMessage({
      type: 'error',
      message: '',
    });
  }, []);

  const showAlert = React.useCallback((message, type = 'error') => {
    setAlertMessage({
      type,
      message,
    });
  }, []);

  React.useEffect(() => {
    if (errorMessage) {
      showAlert(errorMessage);
    } else {
      clearAlert();
    }
  }, [clearAlert, errorMessage, showAlert]);

  const hasActiveFilters = React.useMemo(
    () =>
      Boolean(String(debouncedFilters.searchTerm ?? '').trim()) ||
      Boolean(debouncedFilters.fromDate) ||
      Boolean(debouncedFilters.toDate) ||
      debouncedFilters.status !== 'all',
    [debouncedFilters]
  );

  const scopedLogs = React.useMemo(
    () =>
      logs.filter((log) => {
        if (!debouncedFilters.fromDate && !debouncedFilters.toDate) {
          return true;
        }

        return isDateWithinRange(
          log?.raw?.created_date || log?.createdAt,
          debouncedFilters.fromDate,
          debouncedFilters.toDate
        );
      }),
    [debouncedFilters.fromDate, debouncedFilters.toDate, logs]
  );

  const statusOptions = React.useMemo(() => {
    const counts = logs.reduce((accumulator, log) => {
      const key = String(log?.statusKey ?? 'unknown').trim() || 'unknown';
      accumulator[key] = (accumulator[key] ?? 0) + 1;
      return accumulator;
    }, {});

    const fixedStatusOptions = [
      {
        id: 'all',
        label: 'All',
      },
      {
        id: 'success',
        label: 'Success',
      },
      {
        id: 'failed',
        label: 'Fail',
      },
    ];

    const dynamicStatusOptions = Object.keys(counts)
      .filter((statusKey) => !['success', 'failed'].includes(statusKey))
      .sort((left, right) => left.localeCompare(right))
      .map((statusKey) => ({
        id: statusKey,
        label: formatStatusOptionLabel(statusKey),
      }));

    return [...fixedStatusOptions, ...dynamicStatusOptions];
  }, [logs]);

  const visibleLogs = React.useMemo(() => {
    if (debouncedFilters.status === 'all') {
      return scopedLogs;
    }

    return scopedLogs.filter(
      (log) => String(log?.statusKey ?? 'unknown') === debouncedFilters.status
    );
  }, [debouncedFilters.status, scopedLogs]);

  const totalLoaded = visibleLogs.length;
  const loadedDeliveries = Number(
    stats?.loadedDeliveries ?? totalRecordCount ?? logs.length
  );
  const successfulCount = Number(
    stats?.successful ??
      visibleLogs.filter((log) => log.statusKey === 'success').length
  );
  const failedCount = Number(
    stats?.failed ??
      visibleLogs.filter((log) => log.statusKey === 'failed').length
  );
  const successRate = formatSuccessRate(
    stats?.successRate ??
      (totalLoaded ? (successfulCount / totalLoaded) * 100 : 0)
  );
  const currentPage = Number(
    pagination?.currentPage ?? pagination?.pageNumber ?? pageNumber
  );
  const totalPages = Math.max(1, Number(pagination?.lastPage ?? 1));
  const loadedRangeStart = Number(
    pagination?.from ??
      (totalLoaded > 0 ? (currentPage - 1) * WEBHOOK_PAGE_SIZE + 1 : 0)
  );
  const loadedRangeEnd = Number(
    pagination?.to ??
      (loadedRangeStart > 0 ? loadedRangeStart + totalLoaded - 1 : 0)
  );
  const loadedSummaryLabel =
    totalRecordCount > 0
      ? `Showing ${loadedRangeStart}-${loadedRangeEnd} of ${totalRecordCount} deliveries`
      : 'No deliveries loaded';
  const pageSummaryLabel =
    totalRecordCount > 0
      ? `Page ${currentPage} of ${totalPages}`
      : 'Page 1 of 1';

  const handleCopy = React.useCallback(
    async (value, successMessage) => {
      if (!value) {
        showAlert('Nothing available to copy.');
        return;
      }

      try {
        await navigator.clipboard.writeText(value);
        showAlert(successMessage, 'success');
      } catch (_error) {
        showAlert('Unable to copy to clipboard.');
      }
    },
    [showAlert]
  );

  const selectedLogBase = React.useMemo(() => {
    if (!selectedDeliveryId) {
      return null;
    }

    return (
      logs.find(
        (log) =>
          String(log.deliveryId) === selectedDeliveryId ||
          String(log.key) === selectedDeliveryId
      ) || null
    );
  }, [logs, selectedDeliveryId]);

  const isSelectedLogLoading =
    Boolean(selectedDeliveryId) && loading && logs.length === 0;

  const selectedLog = React.useMemo(() => {
    if (!selectedDeliveryId) {
      return null;
    }

    if (!selectedLogBase) {
      return null;
    }

    return {
      key: selectedLogBase?.key ?? `webhook-log-${selectedDeliveryId}`,
      deliveryId: selectedLogBase?.deliveryId || selectedDeliveryId,
      createdAtLabel: 'Unknown time',
      statusKey: 'unknown',
      statusLabel: 'unknown',
      payloadText: 'Payload unavailable.',
      eventLabel: 'delivery',
      authorLabel: 'Unknown author',
      repoLabel: 'Unknown repository',
      ...selectedLogBase,
    };
  }, [selectedDeliveryId, selectedLogBase]);

  const handleInvestigate = React.useCallback(
    async (log) => {
      const investigationSummary = [
        'Webhook delivery investigation',
        `Delivery ID: ${log.deliveryId}`,
        `Repository: ${log.repoLabel}`,
        `Event: ${log.eventLabel}`,
        `Created: ${log.createdAtLabel}`,
        '',
        log.payloadText,
      ].join('\n');

      await handleCopy(investigationSummary, 'Failure context copied.');
    },
    [handleCopy]
  );

  const handleFilterChange = React.useCallback((key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
  }, []);

  const handleImmediateFilterChange = React.useCallback(
    (key, value) => {
      clearAlert();

      setFilters((current) => {
        const nextFilters = {
          ...current,
          [key]: value,
        };

        setDebouncedFilters(nextFilters);
        return nextFilters;
      });
      setPageNumber(1);
    },
    [clearAlert]
  );

  const handleRefresh = React.useCallback(async () => {
    clearAlert();

    if (!areFiltersEqual(filters, debouncedFilters)) {
      setDebouncedFilters({
        ...filters,
      });
      setPageNumber(1);
      return;
    }

    await refreshLogs();
  }, [clearAlert, debouncedFilters, filters, refreshLogs]);

  const handlePageChange = React.useCallback(
    (nextPage) => {
      clearAlert();
      setPageNumber(nextPage);
    },
    [clearAlert]
  );

  const handleOpenDetails = React.useCallback(
    (log) => {
      clearAlert();
      setSearchParams({ delivery: String(log.deliveryId) });
    },
    [clearAlert, setSearchParams]
  );

  const handleBackToFeed = React.useCallback(() => {
    clearAlert();
    setSearchParams({});
  }, [clearAlert, setSearchParams]);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        clearAlert();
      }}
    >
      <div className="tw rollout-list-page">
        <AppPageSection>
          {alertMessage.message ? (
            <div
              className={cn(
                'rounded-xl border px-4 py-3 text-[13px]',
                getAlertClassName(alertMessage.type)
              )}
            >
              {alertMessage.message}
            </div>
          ) : null}

          {selectedDeliveryId ? (
            selectedLog ? (
              <WebHookLogDetailView
                log={selectedLog}
                onBack={handleBackToFeed}
                onCopyDeliveryId={() =>
                  handleCopy(selectedLog.deliveryId, 'Delivery ID copied.')
                }
                onCopyPayload={() =>
                  handleCopy(selectedLog.payloadText, 'Payload copied.')
                }
                onInvestigate={() => handleInvestigate(selectedLog)}
              />
            ) : isSelectedLogLoading ? (
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="outline"
                  className="border-border/40 bg-transparent hover:bg-accent/20"
                  onClick={handleBackToFeed}
                >
                  Back to logs
                </Button>

                <div className="rounded-2xl border border-border/35 bg-background/90 px-5 py-8 text-center dark:bg-card/25">
                  <RefreshCw className="mx-auto h-5 w-5 animate-spin text-primary" />
                  <div className="mt-3 text-sm font-medium text-foreground">
                    Loading webhook delivery details...
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <Button
                  type="button"
                  variant="outline"
                  className="border-border/40 bg-transparent hover:bg-accent/20"
                  onClick={handleBackToFeed}
                >
                  Back to logs
                </Button>

                <EmptyState
                  icon={Webhook}
                  title="Unable to load this webhook delivery"
                  description="The selected delivery could not be found or its payload is no longer available."
                />
              </div>
            )
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-2xl border border-border/35 bg-background/90 px-5 py-4 dark:bg-card/25">
                  <div className="text-[12px] font-medium text-muted-foreground">
                    Loaded deliveries
                  </div>
                  <div className="mt-2 text-4xl font-semibold text-foreground">
                    {loadedDeliveries}
                  </div>
                </div>

                <div className="rounded-2xl border border-border/35 bg-background/90 px-5 py-4 dark:bg-card/25">
                  <div className="text-[12px] font-medium text-muted-foreground">
                    Successful
                  </div>
                  <div className="mt-2 text-4xl font-semibold text-emerald-700 dark:text-emerald-300">
                    {successfulCount}
                  </div>
                </div>

                <div className="rounded-2xl border border-border/35 bg-background/90 px-5 py-4 dark:bg-card/25">
                  <div className="text-[12px] font-medium text-muted-foreground">
                    Failed
                  </div>
                  <div className="mt-2 text-4xl font-semibold text-rose-700 dark:text-rose-300">
                    {failedCount}
                  </div>
                </div>

                <div className="rounded-2xl border border-border/35 bg-background/90 px-5 py-4 dark:bg-card/25">
                  <div className="text-[12px] font-medium text-muted-foreground">
                    Success rate
                  </div>
                  <div className="mt-2 text-4xl font-semibold text-foreground">
                    {successRate}
                  </div>
                </div>
              </div>

              <WebHookLogFeedFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onImmediateFilterChange={handleImmediateFilterChange}
                onRefresh={handleRefresh}
                refreshing={loading || refreshing}
                statusOptions={statusOptions}
              />

              <div className="flex items-center justify-between gap-3 text-[12px] text-muted-foreground">
                <span>{loadedSummaryLabel}</span>
                <span>{`${visibleLogs.length} deliveries visible on this page`}</span>
              </div>

              {loading && logs.length === 0 ? (
                <WebHookLogFeedSkeleton />
              ) : visibleLogs.length === 0 ? (
                <div className="space-y-4">
                  <EmptyState
                    icon={Webhook}
                    title={
                      hasActiveFilters
                        ? 'No deliveries matched your filters'
                        : 'No webhook deliveries available'
                    }
                    description={
                      hasActiveFilters
                        ? 'Try a different search value or clear the date filters.'
                        : 'Webhook deliveries will appear here once activity is available.'
                    }
                  />
                </div>
              ) : (
                <>
                  <WebHookTimelineFeed
                    logs={visibleLogs}
                    onOpenDetails={handleOpenDetails}
                  />

                  {totalPages > 1 ? (
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      summary={loadedSummaryLabel}
                      className="pt-2"
                    />
                  ) : (
                    <div className="pt-2 text-center text-[12px] text-muted-foreground">
                      {pageSummaryLabel}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </AppPageSection>
      </div>
    </ErrorBoundary>
  );
};

export default WebHookLogs;
